import type {
  BarChart,
  Chart,
  ChartData,
  Chat,
  MultipleBarChart,
  MultipleLineChart,
  PieChart,
  QuickChartData,
} from '@trinity/chat';

export type WidgetTableModel = { headers: string[]; rows: (string | number)[][] };

export type KpiQuickChartData = Extract<QuickChartData, { type: 'kpi-widget' }>;

// widget-chart types that carry a `data.chart` payload; other variants
// (data-table, kpi-widget, progress/semi-circle) do not render the button row
const CHART_SHAPED_WIDGET_TYPES = new Set<string>([
  'bar',
  'stacked-bar',
  'pie',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
  'line',
  'multiple-line',
]);

export function isChartShapedWidgetType(type: string): boolean {
  return CHART_SHAPED_WIDGET_TYPES.has(type);
}

// Build a table (headers + rows) from the live chart payload on-demand
export function buildWidgetTableRows(chartType: string, chart: Chart['chart']): WidgetTableModel {
  switch (chartType) {
    case 'bar':
    case 'stacked-bar': {
      const c = chart as BarChart;
      return {
        headers: [c.xAxisTitle || c.indexBy || 'Category', c.yAxisTitle || 'Value'],
        rows: c.data.map((point) => [point.x, point.y]),
      };
    }

    case 'pie': {
      const c = chart as PieChart;
      const total = c.data.reduce((sum, item) => sum + item.value, 0);
      return {
        headers: ['Label', 'Value', 'Percentage'],
        rows: c.data.map((item) => [
          item.label,
          item.value,
          total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '',
        ]),
      };
    }

    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar': {
      const c = chart as MultipleBarChart;
      const categories = c.data[0]?.data ?? [];
      return {
        headers: [c.xAxisTitle || c.indexBy || 'Category', ...c.data.map((series) => String(series.index))],
        rows: categories.map((point, i) => [point.x, ...c.data.map((series) => series.data[i]?.y ?? '')]),
      };
    }

    case 'line':
    case 'multiple-line': {
      const c = chart as MultipleLineChart;
      const categories = c.data[0]?.data ?? [];
      return {
        headers: [c.xAxisTitle || 'Category', ...c.data.map((line) => line.label ?? line.id)],
        rows: categories.map((point, i) => [String(point.x), ...c.data.map((line) => line.data[i]?.y ?? '')]),
      };
    }

    default:
      return { headers: [], rows: [] };
  }
}

// Adapt a chart-shaped widget attachment into the ChartData the chart contexts/
// hooks consume. `table` stays empty (we render via buildWidgetTableRows);
// `widgetId` is the client-side filter key.
export function widgetChartToChartData(data: QuickChartData, projectId: number, widgetId: string): ChartData {
  const chartPayload = 'chart' in data ? data.chart : undefined;
  return {
    chart: {
      type: data.type as ChartData['chart']['type'],
      chart: chartPayload,
      response: '',
    },
    query: '',
    quantId: 0,
    quantItemId: 0,
    projectId,
    title: chartPayload?.title ?? '',
    table: { columnNames: [], rowNames: [], records: [] },
    footer: [],
    showWarning: false,
    widgetId,
  };
}

/** Build a uniform two-column table for a KPI / metric-card widget. */
export function buildKpiTableRows(values: string[], labels: string[]): WidgetTableModel {
  return {
    headers: ['Metric', 'Value'],
    rows: values.map((value, index) => [labels[index] ?? `KPI ${index + 1}`, value]),
  };
}

/** Build the `kpi-widget` QuickChartData used to save / add a KPI card to a dashboard. */
export function widgetMetricCardToQuickChartData(
  attachment: Chat.Attachment.WidgetMetricCard
): KpiQuickChartData {
  return {
    type: 'kpi-widget',
    title: attachment.title ?? 'KPI Widget',
    backgroundColor: attachment.backgroundColor ?? '#ffffff',
    values: attachment.data.values,
    labels: attachment.data.labels,
  };
}
